import {
  Textarea,
  Image as MantineImage,
  FileButton,
  Button,
  Group,
  Paper,
  Stack,
  Text,
  ActionIcon,
  Grid,
  UnstyledButton,
  useMantineTheme,
} from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { useState } from 'react';

import { ProphetRuleForm } from '../../../hooks/useProphetRuleForm';
import { ChunkPlatform, getChunkUsage } from '../../../utils/chunk-limits';
import { AddButton } from '../../AddButton';
import { RemoveButton } from '../../RemoveButton';

import { prophetTypes } from '@/features/prophet';
import { COLORS } from '@/utils/colors';

// Data shape recap (see prophet/lib/models/rule.ts):
//   messages.<category>: RuleMessage[][]
//     - outer array = VARIATIONS (mutually exclusive — Prophet picks ONE
//       at random per dialog via rule.getSpreadMessages / etc., using
//       sampleWithExclusion). Adding another outer entry means "another
//       alternative wording", NOT another chunk of the same burst.
//     - inner array = the ordered sequence of messages inside the chosen
//       variation. Prophet's send-worker delivers every element of the
//       chosen inner array atomically (one rate-limit slot per element).
//       Adding another inner text element IS a "часть" (chunk) — the
//       actual multi-message burst editors want when a spread text is
//       too long for one Meta message.
//
// Categories below produce a multi-message burst (Prophet spends N slots
// per variation). Their outer cards are labeled "Вариант N", their inner
// text elements are labeled "Часть N", and the inner add-text button
// says "Добавить часть". Greetings and one-shot categories stay generic.
const CHUNK_CATEGORIES: Set<keyof prophetTypes.Rule['messages']> = new Set([
  'spreads',
  'preparing',
  'discounts',
  'spreadKicks',
]);

type MessageProps = {
  message: prophetTypes.RuleMessage;
  placeholder: string;
  form: ProphetRuleForm;
  index: number;
  path: string;
  platform: ChunkPlatform;
  // If set, rendered above the Textarea/image so the editor can tell
  // which chunk of a multi-part burst this element is (e.g. "Часть 2").
  // Only supplied for categories in CHUNK_CATEGORIES.
  partLabel?: string;
};

export const Message = ({
  message,
  placeholder,
  form,
  index,
  path,
  platform,
  partLabel,
}: MessageProps) => {
  const [isOpen, setIsOpen] = useState(false);

  if (message.type === 'image') {
    const source =
      'url' in message ? message.url : message.file ? URL.createObjectURL(message.file) : undefined;
    return (
      <Stack gap={4}>
        {partLabel && (
          <Text size="xs" opacity={0.5}>
            {partLabel} (изображение)
          </Text>
        )}
        <UnstyledButton w="100%" onClick={() => setIsOpen((v) => !v)}>
          <MantineImage
            height={isOpen ? undefined : 100}
            radius="sm"
            fit={isOpen ? 'contain' : 'cover'}
            src={source}
          />
        </UnstyledButton>
      </Stack>
    );
  }

  if (message.type === 'text') {
    // Platform-aware chunk gate. Each text element becomes ONE rate-limited
    // Meta send in Prophet's send-worker. Oversized text would trip the
    // instagram/telegram-server splitter fallback (warn-logged) and silently
    // turn 1 rate-limit slot into 2-3 Meta messages. Editors split long
    // content by adding another INNER text element (button "Добавить часть"
    // at the bottom of this variation card) — NOT by opening another outer
    // variation card (those are mutually exclusive alternatives).
    //
    // Limit is measured in characters for both platforms and is
    // runtime-configurable via GET /config. See utils/chunk-limits.ts.
    const value = (message.text ?? '') as string;
    const usage = getChunkUsage(value, platform);
    const unitLabel = 'символов';
    return (
      <Stack gap={4}>
        {partLabel && (
          <Text size="xs" opacity={0.5}>
            {partLabel}
          </Text>
        )}
        <Textarea
          placeholder={placeholder}
          autosize
          minRows={2}
          error={usage.exceedsLimit ? `Максимум ${usage.limit} ${unitLabel}` : undefined}
          {...form.getInputProps(`${path}.${index}.text`)}
        />
        <Text size="xs" c={usage.exceedsLimit ? 'red' : 'dimmed'} ta="right">
          {usage.current} / {usage.limit} {unitLabel}
        </Text>
      </Stack>
    );
  }

  return null;
};

type MessagesProps = {
  form: ProphetRuleForm;
  messages: prophetTypes.RuleMessage[];
  placeholder: string;
  name: keyof prophetTypes.Rule['messages'];
  index: number;
  platform: ChunkPlatform;
};

export const Messages = ({ messages, placeholder, form, name, index, platform }: MessagesProps) => {
  const path = `messages.${name}.${index}`;
  const showPartLabels = CHUNK_CATEGORIES.has(name);
  const addTextLabel = showPartLabels ? 'Добавить часть' : 'Text';

  const onFileSelect = (file: File | null) => {
    if (file) {
      form.insertListItem(path, { type: 'image', file });
    }
  };

  return (
    <>
      {messages.map((message, i) => (
        <Grid key={i} align="center">
          <Grid.Col span={10}>
            <Message
              message={message}
              placeholder={placeholder}
              form={form}
              index={i}
              path={path}
              platform={platform}
              partLabel={showPartLabels ? `Часть ${i + 1}` : undefined}
            />
          </Grid.Col>
          <Grid.Col span={2}>
            <RemoveButton onClick={() => form.removeListItem(path, i)} />
          </Grid.Col>
        </Grid>
      ))}
      {!(name === 'greetings' && messages.length >= 1) && (
        <Group justify="center">
          <Button.Group>
            <Button
              variant="default"
              size="compact-sm"
              onClick={() => form.insertListItem(path, { type: 'text', text: '' })}
            >
              {addTextLabel}
            </Button>
            {name !== 'greetings' && (
              <FileButton accept="image/png,image/jpeg" onChange={onFileSelect}>
                {(props) => (
                  <Button
                    style={{
                      borderTopRightRadius: 'var(--mantine-radius-sm)',
                      borderBottomRightRadius: 'var(--mantine-radius-sm)',
                    }}
                    variant="default"
                    size="compact-sm"
                    {...props}
                  >
                    Image
                  </Button>
                )}
              </FileButton>
            )}
          </Button.Group>
        </Group>
      )}
    </>
  );
};

type Props = {
  form: ProphetRuleForm;
  name: keyof prophetTypes.Rule['messages'];
  placeholder: string;
  isRequired?: boolean;
  platform: ChunkPlatform;
};

// Outer card label. For chunked categories (spreads etc.), an outer card
// is a mutually-exclusive VARIATION — Prophet picks one at random. Adding
// a new card means "another alternative wording of the whole burst", not
// "another chunk". Greetings and one-shot categories keep the generic "#".
const VARIATION_LABEL: Partial<Record<keyof prophetTypes.Rule['messages'], string>> = {
  spreads: 'Вариант',
  preparing: 'Вариант',
  discounts: 'Вариант',
  spreadKicks: 'Вариант',
};

export const MessagesFields = ({ form, name, placeholder, platform }: Props) => {
  const theme = useMantineTheme();
  const fields = form.values.messages[name];
  const path = `messages.${name}`;
  const variationLabel = VARIATION_LABEL[name] ?? '#';
  return (
    <>
      {fields?.map((messages, index) => {
        return (
          <Paper key={index} my="sm" radius="sm" bg={COLORS.accentBackground} p="sm">
            <Group justify="space-between" mb="xs">
              <Text size="sm" opacity={0.6}>
                {variationLabel} {index + 1}
              </Text>
              <ActionIcon
                color={theme.colors.dark[0]}
                variant="transparent"
                onClick={() => form.removeListItem(path, index)}
              >
                <IconX opacity={0.6} size={16} />
              </ActionIcon>
            </Group>
            <Stack>
              <Messages
                name={name}
                messages={messages}
                placeholder={placeholder}
                form={form}
                index={index}
                platform={platform}
              />
            </Stack>
          </Paper>
        );
      })}
      <AddButton size={25} onClick={() => form.insertListItem(path, [])} />
    </>
  );
};
